//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { SamplingCampaignRequestCountry } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';

export enum SamplingFulfillmentOptionProductEnum {
  RATE_ZONE_3 = 'rateZone3',
  UK = 'uk',
  US_RATE_1 = 'usRate1',
  RATE_ZONE_2 = 'rateZone2',
  RATE_ZONE_4 = 'rateZone4',
  RATE_ZONE_1 = 'rateZone1',
  CANADA = 'canada',
  AUSTRALIA = 'australia',
  UK_ALCOHOL = 'ukAlchohol',
  AUS_ALCOHOL = 'ausAlchohol',
}

export type SamplingFulfillmentOptionProductName =
  | 'Sampling Fulfillment - Rate Zone 3'
  | 'Sampling Fulfillment - UK'
  | 'Sampling Fulfillment - US Rate 1'
  | 'Sampling Fulfillment - Rate Zone 2'
  | 'Sampling Fulfillment - Rate Zone 4'
  | 'Sampling Fulfillment - Rate Zone 1'
  | 'Sampling Fulfillment - Canada'
  | 'Sampling Fulfillment - Australia'
  | 'Sampling Fulfillment - UK: Alcohol'
  | 'Sampling Fulfillment - Australia: Alcohol';

export type SamplingFulfillmentOptionProductDescription =
  | 'Provides fulfillment services in Portugal, Poland, Denmark, Latvia, Sweden, Lithuania, and Italy'
  | 'Provides fulfillment services in UK (excluding N. Ireland)'
  | 'US sampling fulfillment and shipping. Clients ship products to fulfillment provider'
  | 'Provides fulfillment services in Czech Republic, Spain, Slovakia, Hungary, and Austria'
  | 'Provides fulfillment services in Iceland, Greece, Finland, and Norway'
  | 'Provides fulfillment services in Belgium, Netherlands, Ireland, Germany, and France'
  | 'Provides fulfillment services in Canada'
  | 'Provides fulfillment services in Australia'
  | 'Provides fulfillment services in UK (Alcohol)'
  | 'Provides fulfillment services in Australia (Alcohol)';

export type SamplingFulfillmentProductCode =
  | '01t2J000005WhtGQAS'
  | '01t2J000005Wht6QAC'
  | '01t2J000005Wht8QAC'
  | '01t2J000005Wht9QAC'
  | '01t2J000005WhtBQAS'
  | '01t2J000005WhtDQAS'
  | '01t2J000005Wht7QAC'
  | '01t2J000005WhtAQAS'
  | '01tHt0000070iTsIAI'
  | '01tHt0000070iTyIAI';

export type SamplingShippingWeightMetric =
  | '<.5 kg'
  | '.5-2 kg'
  | '2-5 kg'
  | '5-8 kg'
  | '>8 kg'
  | '<1 kg'
  | '1-2 kg'
  | '2-3 kg'
  | '3-4 kg'
  | '4-5 kg'
  | '5-6 kg'
  | '6-7 kg'
  | '7-8 kg';

export type SamplingShippingWeightImperial =
  | '<8 oz'
  | '<1 lb'
  | '1-2 lb'
  | '2-3 lb'
  | '3-4 lb'
  | '4-5 lb'
  | '5-6 lb'
  | '6-8 lb'
  | '8-10 lb'
  | '>10 lb';

export type SamplingShippingWeight =
  | SamplingShippingWeightMetric
  | SamplingShippingWeightImperial;

export const SAMPLING_SHIPPING_WEIGHT_METRIC_ORDER: SamplingShippingWeightMetric[] =
  [
    '<.5 kg',
    '.5-2 kg',
    '2-5 kg',
    '5-8 kg',
    '>8 kg',
    '<1 kg',
    '1-2 kg',
    '2-3 kg',
    '3-4 kg',
    '4-5 kg',
    '5-6 kg',
    '6-7 kg',
    '7-8 kg',
  ];

export const SAMPLING_SHIPPING_WEIGHT_IMPERIAL_ORDER: SamplingShippingWeightImperial[] =
  [
    '<8 oz',
    '<1 lb',
    '1-2 lb',
    '2-3 lb',
    '3-4 lb',
    '4-5 lb',
    '5-6 lb',
    '6-8 lb',
    '8-10 lb',
    '>10 lb',
  ];

export const SAMPLING_SHIPPING_WEIGHT_ORDER: SamplingShippingWeight[] = [
  ...SAMPLING_SHIPPING_WEIGHT_METRIC_ORDER,
  ...SAMPLING_SHIPPING_WEIGHT_IMPERIAL_ORDER,
];

export interface SamplingFulfillmentOption {
  availableCountryCodes?: SamplingCampaignRequestCountry[];
  availableShippingWeights?: SamplingShippingWeight[];
  cartonCount?: number;
  cartonType?: string;
  id?: string;
  insert?: string;
  productDescription?: SamplingFulfillmentOptionProductDescription;
  productEnum?: SamplingFulfillmentOptionProductEnum;
  productName?: SamplingFulfillmentOptionProductName;
  sfdcProductCode?: SamplingFulfillmentProductCode;
  shippingWeight?: SamplingShippingWeight;
}

export enum SamplingContentType {
  REVIEWS_ONLY = 'REVIEWS_ONLY',
  SOCIAL_ONLY = 'SOCIAL_ONLY',
  REVIEWS_AND_SOCIAL = 'REVIEWS_AND_SOCIAL',
}

export enum SamplingDeliveryMethod {
  Shipped = 'Shipped',
  ShippedByBazaarvoice = 'Shipped by Bazaarvoice',
  DigitalDelivery = 'Digital Delivery',
}

export interface PurchaseOption {
  areUnitsFlexible?: boolean;
  attributes?: string;
  balance?: number;
  contentType?: SamplingContentType;
  created?: Date;
  deliveryMethod?: SamplingDeliveryMethod;
  expiryDate?: Date;
  fulfillmentOptions?: SamplingFulfillmentOption[];
  hasExternalReview?: boolean;
  hasPostSurvey?: boolean;
  hasSocialCta?: boolean;
  opportunityId: string;
  opportunityRef?: string;
  sfdcProductType?: string;
  unitCount?: number;
  updated?: Date;
}

export interface SamplingOpportunitiesResponse {
  limit: number;
  offset: number;
  opportunities: PurchaseOption[];
  total: number;
}

export interface GetSamplingOpportunitiesResponse {
  data: SamplingOpportunitiesResponse;
}

export enum PurchaseOptionUsage {
  MULTI_USE = 'Multi-Use',
  SINGLE_USE = 'Single Use',
}

export interface ClientPurchaseOptionsSummary {
  opportunitiesAccounted?: string[];
  unitsApproved?: number;
  unitsAvailable?: number;
  unitsExpired?: number;
  unitsPurchased?: number;
  unitsRejected?: number;
  unitsReviewed?: number;
  unitsSelected?: number;
  unitsShipped?: number;
  unitsWontShip?: number;
}

export interface GetClientPurchaseOptionsSummaryResponse {
  data: ClientPurchaseOptionsSummary;
}

export interface GetClientPurchaseOptionsSummaryRequest {
  showAllUnitsForUltimateParentAccount?: boolean;
}
